/**
 * AppShell - top-level page chrome.
 *
 * Desktop keeps the sidebar model. Mobile becomes a native-style operator shell
 * with a compact top bar, scroll-aware dock, and a utility sheet for lower
 * traffic destinations.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ArrowRight, Grid2x2, X } from 'lucide-react';
import { useWhoami } from '../lib/auth';
import { AppSidebar } from './AppSidebar';
import { UserMenu, PinUnlockedBadge } from './UserMenu';
import { SECTION_GROUPS, SECTION_LIST, sectionOf } from './sections';

const ReadOnlyCtx = createContext(false);

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');

export function useReadOnly() {
  return useContext(ReadOnlyCtx);
}

export function AppShell({
  title,
  actions,
  toolbar,
  children,
}: {
  title: string;
  actions?: React.ReactNode;
  toolbar?: React.ReactNode;
  children: React.ReactNode;
}) {
  const who = useWhoami();
  const location = useLocation();
  const isBoss = who.data?.state === 'authenticated' && who.data.role === 'boss';
  const pinUntil = who.data?.state === 'authenticated' ? who.data.pinUnlockedUntil : null;
  const [moreOpen, setMoreOpen] = useState(false);
  const [mobileDockVisible, setMobileDockVisible] = useState(true);
  const [chromeHeights, setChromeHeights] = useState({ top: 88, dock: 98 });
  const headerRef = useRef<HTMLElement | null>(null);
  const dockRef = useRef<HTMLElement | null>(null);
  const lastScrollYRef = useRef(0);

  const primaryTabs = SECTION_LIST.filter((s) => s.primaryTab);
  const activeSection = useMemo(() => {
    const activeKey = sectionOf(location.pathname);
    return SECTION_LIST.find((item) => item.key === activeKey) ?? null;
  }, [location.pathname]);
  const ActiveIcon = activeSection?.icon;
  const sectionColor = activeSection?.color ?? 'var(--brand)';
  const sectionTint = activeSection?.tint ?? 'var(--brand-light)';
  const sectionCanvas = activeSection?.canvas ?? 'var(--bg)';
  const sectionSurface = activeSection?.surface ?? 'var(--surface)';
  const sectionPlate = activeSection?.plate ?? 'var(--surface-raised)';
  const sectionDock = activeSection?.dock ?? 'var(--surface-contrast)';
  const sectionEdge = activeSection?.edge ?? 'var(--border)';

  useEffect(() => {
    setMoreOpen(false);
    setMobileDockVisible(true);
    lastScrollYRef.current = window.scrollY;
  }, [location.pathname]);

  useEffect(() => {
    const measure = () => {
      setChromeHeights({
        top: headerRef.current?.offsetHeight ?? 88,
        dock: dockRef.current?.offsetHeight ?? 98,
      });
    };

    measure();

    const ro = new ResizeObserver(() => measure());
    if (headerRef.current) ro.observe(headerRef.current);
    if (dockRef.current) ro.observe(dockRef.current);
    window.addEventListener('resize', measure);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  useEffect(() => {
    let frame = 0;

    const update = () => {
      frame = 0;
      if (window.matchMedia('(min-width: 768px)').matches) {
        setMobileDockVisible(true);
        lastScrollYRef.current = window.scrollY;
        return;
      }

      const currentY = window.scrollY;
      const delta = currentY - lastScrollYRef.current;

      if (currentY < 56 || delta < -8) {
        setMobileDockVisible(true);
      } else if (delta > 10 && currentY > chromeHeights.top + 48) {
        setMobileDockVisible(false);
      }

      lastScrollYRef.current = currentY;
    };

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    lastScrollYRef.current = window.scrollY;
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [chromeHeights.top, location.pathname]);

  return (
    <ReadOnlyCtx.Provider value={isBoss}>
      <div
        className="relative min-h-screen overflow-x-clip bg-bg font-ui text-ink"
        style={
          {
            '--mobile-topbar-height': `${chromeHeights.top}px`,
            '--mobile-dock-height': `${chromeHeights.dock}px`,
            '--active-section-color': sectionColor,
            '--active-section-tint': sectionTint,
            '--active-section-canvas': sectionCanvas,
            '--active-section-surface': sectionSurface,
            '--active-section-plate': sectionPlate,
            '--active-section-dock': sectionDock,
            '--active-section-edge': sectionEdge,
            '--bg': sectionCanvas,
            '--surface': sectionSurface,
            '--surface-raised': sectionPlate,
            '--surface-contrast': sectionDock,
            '--border': sectionEdge,
            '--border-strong': sectionEdge,
          } as CSSProperties
        }
      >
        <div
          aria-hidden
          className="pointer-events-none fixed inset-0 -z-10"
          style={{
            background: [
              `radial-gradient(circle at 7% -1%, ${sectionTint} 0%, transparent 34%)`,
              `radial-gradient(circle at 96% 2%, ${sectionPlate} 0%, transparent 24%)`,
              `linear-gradient(180deg, rgba(255,255,255,0.16) 0%, transparent 34%)`,
            ].join(', '),
            opacity: 0.78,
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none fixed inset-x-0 top-0 -z-10 h-[240px]"
          style={{
            background: `linear-gradient(180deg, ${sectionTint} 0%, transparent 100%)`,
            opacity: 0.34,
          }}
        />

        <AppSidebar />

        <header
          ref={headerRef}
          className="sticky top-0 z-30 border-b border-border backdrop-blur-2xl md:hidden"
          style={{
            background: `linear-gradient(180deg, rgba(255,255,255,0.82) 0%, ${sectionCanvas} 100%)`,
          }}
        >
          <div className="pt-[env(safe-area-inset-top)]">
            <div className="mx-auto flex min-h-[76px] max-w-7xl items-center justify-between gap-3 px-4 py-4 sm:px-6">
              <div className="min-w-0 flex-1">
                <div
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-semibold uppercase tracking-[0.16em] text-white shadow-sm"
                  style={{
                    background: 'linear-gradient(135deg, rgba(15,23,41,0.98) 0%, rgba(32,46,72,0.95) 100%)',
                  }}
                >
                  <span
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ background: sectionColor }}
                    aria-hidden
                  />
                  SRB Accounts
                </div>
                <div className="mt-2.5 flex min-w-0 items-center gap-3">
                  {ActiveIcon ? (
                    <span
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[16px] border border-border shadow-sm"
                      style={{
                        color: sectionColor,
                        background: `linear-gradient(150deg, rgba(255,255,255,0.42) 0%, ${sectionSurface} 100%)`,
                      }}
                      aria-hidden
                    >
                      <ActiveIcon className="h-[19px] w-[19px]" strokeWidth={2.05} />
                    </span>
                  ) : null}
                  <div className="min-w-0">
                    <div className="truncate text-[1.34rem] font-semibold tracking-[-0.03em] text-ink">
                      {activeSection?.label ?? title}
                    </div>
                    <div className="truncate text-[0.92rem] leading-[1.4rem] text-ink-muted">
                      {activeSection?.mobileHint ?? 'Daily finance operations'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-2">
                <div className="hidden sm:block">
                  {pinUntil && <PinUnlockedBadge unlockedUntil={pinUntil} />}
                </div>
                <button
                  type="button"
                  onClick={() => setMoreOpen(true)}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-border bg-surface text-ink-muted shadow-sm backdrop-blur-xl transition-all duration-200 hover:-translate-y-px hover:bg-surface-raised hover:text-ink"
                  aria-label="Open more destinations"
                  style={{ boxShadow: `0 18px 32px -24px ${sectionColor}` }}
                >
                  <Grid2x2 className="h-[18px] w-[18px]" strokeWidth={2} />
                </button>
                <UserMenu align="right" compact />
              </div>
            </div>
          </div>
        </header>

        <div className="min-w-0 md:pl-[212px]">
          {toolbar}
          <main className="mx-auto min-w-0 max-w-7xl overflow-x-clip px-4 pb-[calc(var(--mobile-dock-height,6.2rem)+1.55rem+env(safe-area-inset-bottom))] pt-4 sm:px-6 md:pb-10 md:pt-6">
            {!toolbar && (
              <div className="native-screen-enter mb-4 flex flex-col gap-3">
                <div>
                  <div className="text-caption font-medium uppercase tracking-[0.12em] text-ink-muted">Workspace</div>
                  <h1 className="mt-1.5 text-[2.24rem] font-semibold tracking-[-0.04em] text-ink">
                    {title}
                  </h1>
                </div>
                {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
              </div>
            )}
            {children}
          </main>
        </div>

        <nav
          ref={dockRef}
          aria-label="Main navigation"
          className={cx(
            'fixed inset-x-3 z-30 transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] md:hidden',
            mobileDockVisible ? 'translate-y-0 opacity-100' : 'translate-y-[calc(100%+1rem)] opacity-0 pointer-events-none',
          )}
          style={{ bottom: 'max(0.85rem, env(safe-area-inset-bottom))' }}
        >
          <div
            className="flex items-center gap-1 rounded-[32px] border border-border p-2 shadow-[0_24px_70px_-36px_rgba(15,23,42,0.45)] backdrop-blur-2xl"
            style={{
              background: `linear-gradient(180deg, rgba(255,255,255,0.38) 0%, ${sectionDock} 100%)`,
            }}
          >
            {primaryTabs.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.key}
                  to={item.to}
                  end={item.to === '/'}
                  className="group flex min-w-0 flex-1 items-center justify-center"
                >
                  {({ isActive }) => (
                    <span
                      className="flex min-w-0 flex-col items-center justify-center gap-1.5 rounded-[22px] px-2 py-2.5 transition-all duration-200"
                      style={
                        isActive
                          ? {
                              background: `linear-gradient(180deg, rgba(255,255,255,0.52) 0%, ${item.surface} 100%)`,
                              boxShadow: `0 16px 28px -22px ${item.color}`,
                            }
                          : undefined
                      }
                    >
                      <span
                        className="flex h-9 w-9 items-center justify-center rounded-full transition-colors"
                        style={{
                          background: isActive ? item.color : item.surface,
                          color: isActive ? '#ffffff' : item.color,
                          border: `1px solid ${isActive ? 'transparent' : item.edge}`,
                        }}
                        aria-hidden
                      >
                        <Icon className="h-[18px] w-[18px]" strokeWidth={1.95} />
                      </span>
                      <span
                        className="max-w-full truncate text-[0.78rem] font-semibold"
                        style={{ color: isActive ? 'var(--ink)' : item.color }}
                      >
                        {item.label}
                      </span>
                    </span>
                  )}
                </NavLink>
              );
            })}
          </div>
        </nav>

        {moreOpen && (
          <div
            className="fixed inset-0 z-40 flex items-end bg-[rgba(15,23,42,0.32)] backdrop-blur-[3px] md:hidden"
            onClick={() => setMoreOpen(false)}
          >
            <div
              className="max-h-[78vh] w-full overflow-y-auto rounded-t-[32px] border-t border-border p-4 pb-6 shadow-[0_-16px_40px_-28px_rgba(15,23,42,0.45)] backdrop-blur-2xl"
              style={{
                background: `linear-gradient(180deg, ${sectionPlate} 0%, ${sectionSurface} 100%)`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-surface-raised" />
              <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                  <div className="text-caption font-medium uppercase tracking-[0.12em]" style={{ color: sectionColor }}>
                    Utilities
                  </div>
                  <div className="text-[1.28rem] font-semibold tracking-[-0.03em] text-ink">
                    Setup and preferences
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setMoreOpen(false)}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-border bg-surface text-ink-muted shadow-sm transition-colors hover:bg-surface-raised hover:text-ink"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" strokeWidth={2} />
                </button>
              </div>

              <div className="space-y-4">
                {SECTION_GROUPS.filter((group) => group.items.some((item) => !item.primaryTab)).map((group) => (
                  <div key={group.label} className="space-y-2.5">
                    <div className="px-1 text-caption font-medium uppercase tracking-[0.14em] text-ink-muted">
                      {group.label}
                    </div>
                    {group.items.filter((item) => !item.primaryTab).map((item) => {
                      const Icon = item.icon;
                      return (
                        <NavLink
                          key={item.key}
                          to={item.to}
                          end={item.to === '/'}
                          onClick={() => setMoreOpen(false)}
                          className="flex items-center gap-3 rounded-[24px] border border-border px-4 py-3.5 shadow-sm transition-transform duration-150 hover:-translate-y-px"
                          style={({ isActive }) => ({
                            background: isActive
                              ? `linear-gradient(160deg, rgba(255,255,255,0.38) 0%, ${item.plate} 100%)`
                              : `linear-gradient(180deg, rgba(255,255,255,0.24) 0%, ${item.surface} 100%)`,
                            borderColor: isActive ? item.edge : 'var(--border)',
                          })}
                        >
                          <span
                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
                            style={{ background: item.plate, color: item.color, border: `1px solid ${item.edge}` }}
                            aria-hidden
                          >
                            <Icon className="h-[18px] w-[18px]" strokeWidth={1.9} />
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="text-body font-semibold text-ink">{item.label}</div>
                            <div className="text-small leading-5 text-ink-muted">{item.mobileHint}</div>
                          </div>
                          <ArrowRight className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.9} />
                        </NavLink>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </ReadOnlyCtx.Provider>
  );
}
